import {
  EMPTY_HISTORY,
  Evaluator,
  ScriptError,
  UNITS,
  canonical,
  parseTemplate,
  serialize,
  templateDescription,
  withProp,
  workoutExercises,
  type History,
  type TemplateNode,
} from '@gymcloud/mixedscript'
import { Link, createFileRoute, useBlocker, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '~/api/client'
import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { reached, useBeat } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { CodeIcon, PlayIcon, RedoIcon, UndoIcon } from '~/components/icons'
import { TemplateBoard } from '~/components/template-board'
import { TemplateTrialPanel } from '~/components/template-trial-panel'
import { WholeNumberField } from '~/components/whole-number-field'
import { CodeEditor } from '~/components/code-editor'
import { useDataService, useDataState } from '~/data/data-context'
import { blankScript, toHistory } from '~/data/templates'
import { DIALOG_PATHS } from '~/lib/dialogs'
import { effectiveRecentExercises } from '~/lib/exercise-insights'
import { setExerciseNames } from '~/lib/code-problems'
import { EMPTY_TRIAL, type Trial } from '~/lib/template-trial'
import { cycleOf, dayOf, inDayOrder, number, scheduleOf } from '~/lib/template-tree'

export const Route = createFileRoute('/templates_/$templateId_/edit')({ component: TemplateEditor })

const SCHEDULES = ['free', 'sequential', 'strict'] as const

const SCHEDULE_CAPTION: Record<string, string | undefined> = {
  free: 'Every workout is offered — do whichever one you like.',
  sequential: 'Workouts run in order, wrapping past the end.',
  strict: 'Each workout sits on a weekday of a repeating cycle.',
}

/** Which tool the drawer has open, if any. */
type Panel = 'script' | 'trial'

/** How many edits the editor keeps to walk back through. */
const UNDO_DEPTH = 100

/**
 * The template editor — `@specs/features/workout.spec.md`, The template editor.
 *
 * A workspace rather than a page: the board takes the width, and the tools it needs — the
 * script it writes, the sessions it leads to — live on a rail at the edge and slide in as a
 * full-height drawer. The script is the artifact, so every change goes through the tree and
 * comes back as canonical text, which is also what the script drawer edits directly.
 */
function TemplateEditor() {
  const { templateId } = useParams({ from: '/templates_/$templateId_/edit' })
  const state = useDataState()
  const service = useDataService()
  const navigate = useNavigate()
  const { units } = useAuth()

  // "new" is a draft with no row behind it; the library learns of it when it is saved.
  const creating = templateId === 'new'
  const template = state.templates.find((held) => held.id === templateId)
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  // The description opens up while it is being written and folds back to its one line after.
  const [describing, setDescribing] = useState(false)
  const [publishTrouble, setPublishTrouble] = useState<string | null>(null)
  const [publishing, setPublishing] = useState(false)
  const publishingShown = useBeat(publishing)
  const [unpublishing, setUnpublishing] = useState(false)
  const targetId = creating ? createdId : templateId

  const [script, setScript] = useState(() => (creating ? blankScript('New template') : ''))
  const [panel, setPanel] = useState<Panel | null>(null)
  // The trial is the editor's own: it lasts as long as the editor is open, and is never saved.
  const [trial, setTrial] = useState<Trial>(EMPTY_TRIAL)
  // What the drawer shows while it slides shut is whatever it was showing.
  const [heldPanel, setHeldPanel] = useState<Panel | null>(null)
  const [seenPanel, setSeenPanel] = useState<Panel | null>(null)
  if (seenPanel !== panel) {
    setSeenPanel(panel)
    if (panel !== null) {
      setHeldPanel(panel)
    }
  }
  useEffect(() => {
    if (panel !== null) {
      return
    }
    const timer = setTimeout(() => {
      setHeldPanel(null)
    }, 300)
    return () => {
      clearTimeout(timer)
    }
  }, [panel])

  // What the library holds for this template. Saved is the script matching it — byte for
  // byte, or canonically: a change made and unmade reads as no change, even though the first
  // touch reformatted a hand-written file.
  const [savedText, setSavedText] = useState<string | null>(null)
  const savedCanon = useMemo(() => {
    if (savedText === null) {
      return null
    }
    try {
      return canonical(savedText)
    } catch {
      // A saved script that does not parse has no canonical form to compare against.
      return null
    }
  }, [savedText])
  const matchesSaved = (text: string): boolean => {
    if (savedText === null) {
      return false
    }
    if (text === savedText) {
      return true
    }
    if (savedCanon === null) {
      return false
    }
    try {
      return canonical(text) === savedCanon
    } catch {
      return false
    }
  }
  // The blocker reads this rather than derived state, so a navigation issued in the same tick
  // as a save — discarding, for one — sees the change that has just been made.
  const dirty = useRef(false)

  // Which template's script this editor holds. A template arriving from the library — a deep
  // link landing before the fetch, most of all — is adopted the moment it appears, unless the
  // editor already holds changes of its own.
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const [past, setPast] = useState<string[]>([])
  const [future, setFuture] = useState<string[]>([])
  const lastHold = useRef<{ at: number; source: 'board' | 'script' }>({ at: 0, source: 'board' })
  if (!creating && template !== undefined && loadedFor !== template.id) {
    setLoadedFor(template.id)
    setScript(template.script)
    setSavedText(template.script)
    setPast([])
    setFuture([])
  }
  // A template just adopted is exactly what the library holds, so nothing is unsaved. The
  // blocker only ever asks after a commit, so telling it here is soon enough.
  useEffect(() => {
    dirty.current = false
  }, [loadedFor])

  // Not in the library yet: it may still be on its way from the server. Ask once, and only
  // call it missing after the answer comes back.
  const missing = !creating && template === undefined
  const [checked, setChecked] = useState(false)
  useEffect(() => {
    if (!missing) {
      return
    }
    let alive = true
    service
      .refresh()
      .catch(() => undefined)
      .finally(() => {
        if (alive) {
          setChecked(true)
        }
      })
    return () => {
      alive = false
    }
  }, [missing, service])

  const node = useMemo(() => {
    try {
      return parseTemplate(script)
    } catch {
      return null
    }
  }, [script])
  // What is wrong with the script and where, for the drawer's squiggle and the status line.
  const problemInfo = useMemo(() => {
    if (script === '' || node !== null) {
      return null
    }
    try {
      parseTemplate(script)
      return null
    } catch (error) {
      return error instanceof ScriptError
        ? { line: error.line, column: error.column, message: error.located }
        : { line: 1, column: 1, message: String(error) }
    }
  }, [script, node])
  const problem = problemInfo === null ? null : problemInfo.message
  const saved = matchesSaved(script)

  // The board keeps showing the last script that parsed, dimmed, while the text is broken —
  // a wall of nothing would make a typo feel like a loss.
  const [lastGood, setLastGood] = useState<TemplateNode | null>(null)
  if (node !== null && node !== lastGood) {
    setLastGood(node)
  }

  const hold = (next: string, source: 'board' | 'script') => {
    if (next === script) {
      return
    }
    // A board edit that only reformats what was already written changes nothing anybody said —
    // the first touch of a hand-written script must not read as an edit.
    if (source === 'board') {
      try {
        if (next === canonical(script)) {
          return
        }
      } catch {
        // A script that does not parse has no canonical form; any change to it is real.
      }
    }
    // Reading the clock is impure, and the compiler cannot see that `hold` only ever runs
    // from an event — a keystroke, a drag, a press. Nothing here is called while rendering.
    // eslint-disable-next-line react-hooks/purity
    const at = Date.now()
    // A burst of typing settles into one undo step rather than one per keystroke. The board's
    // window is tight — keystrokes land well inside it, deliberate clicks do not.
    const coalesce =
      lastHold.current.source === source &&
      at - lastHold.current.at < (source === 'script' ? 800 : 300)
    if (!coalesce) {
      setPast((held) => [...held.slice(1 - UNDO_DEPTH), script])
      setFuture([])
    }
    lastHold.current = { at, source }
    setScript(next)
    dirty.current = !matchesSaved(next)
  }

  const rewrite = (next: TemplateNode) => {
    // A strict template is its calendar, so what it holds is kept in the order the days fall
    // — see `lib/template-tree.inDayOrder`.
    hold(serialize(inDayOrder(next)), 'board')
  }

  const restore = (next: string) => {
    lastHold.current = { at: 0, source: 'board' }
    setScript(next)
    dirty.current = !matchesSaved(next)
  }

  const undo = () => {
    const previous = past[past.length - 1]
    if (previous === undefined) {
      return
    }
    setPast(past.slice(0, -1))
    setFuture([script, ...future])
    restore(previous)
  }

  const redo = () => {
    const next = future[0]
    if (next === undefined) {
      return
    }
    setFuture(future.slice(1))
    setPast([...past, script])
    restore(next)
  }

  const save = async (): Promise<void> => {
    const kept = await service.saveTemplate(targetId, script)
    setSavedText(script)
    dirty.current = false
    setCreatedId(kept.id)
    setLoadedFor(kept.id)
    if (creating && createdId === null) {
      // The draft has a row behind it now; the address should say which one.
      void navigate({
        to: '/templates/$templateId/edit',
        params: { templateId: kept.id },
        replace: true,
      })
    }
  }

  /**
   * Publishing what is written, saving it first if it is not saved: what goes into Explore is
   * the script as it stands, and leaving the two disagreeing would publish something nobody
   * had in front of them.
   */
  const publishThis = async (): Promise<void> => {
    if (publishing) return
    setPublishing(true)
    setPublishTrouble(null)
    try {
      if (!saved) {
        await save()
      }
      const id = targetId ?? createdId
      if (id !== null) {
        await service.publishTemplate(id)
      }
    } catch (error) {
      setPublishTrouble(
        error instanceof ApiError
          ? `It will not publish: ${error.message}`
          : 'Publishing needs a connection.',
      )
    } finally {
      setPublishing(false)
    }
  }

  /** Taking it out of Explore. Everybody already running it carries on where they are. */
  const unpublishThis = async (): Promise<void> => {
    const id = targetId ?? createdId
    if (id === null) {
      return
    }
    // Said in the confirmation it was pressed from, which stays to be tried again.
    await reached(() => service.unpublishTemplate(id), 'Unpublishing needs a connection.')
    setPublishTrouble(null)
  }

  // What would go wrong planning each workout as it stands. Saving is still allowed — a
  // template mid-edit is allowed to be unfinished — but nobody should find out at the gym.
  const troubles = useMemo(() => {
    if (node === null) {
      return []
    }
    const out = new Map<string, string | null>()
    const workoutAnchor = (index: number) => `board-workout-${String(index)}`
    // Where an exercise named in a plan sits, so clicking its error lands on its card.
    const exerciseAnchor = (workoutIndex: number, name: string): string | null => {
      const workout = node.workouts[workoutIndex]
      if (workout === undefined) {
        return null
      }
      for (const [itemIndex, item] of workout.items.entries()) {
        const members = item.type === 'exercise' ? [item.exercise] : item.exercises
        for (const [within, member] of members.entries()) {
          if (name === '' ? member.name.trim() === '' : member.name === name) {
            return `board-ex-${String(workoutIndex)}-${String(itemIndex)}-${String(within)}`
          }
        }
      }
      return workoutAnchor(workoutIndex)
    }

    // The calendar's own rules, said in the board's language — workouts and weekdays, not
    // script lines and columns.
    const strictHere = scheduleOf(node) === 'strict'
    const cycle = cycleOf(node)
    if (strictHere) {
      const taken = new Map<number, string>()
      node.workouts.forEach((workout, index) => {
        const label = workout.name.trim() === '' ? 'A workout' : `“${workout.name}”`
        const day = dayOf(workout)
        if (day === null) {
          out.set(`${label} has no day on the calendar yet`, workoutAnchor(index))
          return
        }
        if (cycle !== null && (day < 0 || day >= cycle)) {
          out.set(
            `${label} sits past the cycle's end — give the cycle more weeks or move it in`,
            workoutAnchor(index),
          )
        }
        const other = taken.get(day)
        if (other !== undefined) {
          out.set(`${other} and ${label} share the same day`, workoutAnchor(index))
        } else {
          taken.set(day, label)
        }
      })
    } else {
      if (node.workouts.some((workout) => dayOf(workout) !== null)) {
        out.set('A workout names a day, but only a strict schedule has days', null)
      }
      if (cycleOf(node) !== null) {
        out.set('The template names a cycle, but only a strict schedule has one', null)
      }
    }

    const seen = new Set<string>()
    node.workouts.forEach((workout, index) => {
      const label = workout.name.trim()
      if (label === '') {
        out.set('A workout has no name yet', workoutAnchor(index))
      } else if (seen.has(label)) {
        out.set(`Two workouts are both called “${label}”`, workoutAnchor(index))
      } else {
        seen.add(label)
      }
      if (workoutExercises(workout).some((exercise) => exercise.name.trim() === '')) {
        out.set(
          `${label === '' ? 'A workout' : label} holds an exercise with no name yet`,
          exerciseAnchor(index, ''),
        )
      }
    })
    // The lines themselves: a range running downward, a line that plans nothing, a count
    // nobody meant.
    node.workouts.forEach((workout, index) => {
      workout.items.forEach((item) => {
        const members = item.type === 'exercise' ? [item.exercise] : item.exercises
        for (const member of members) {
          const label = member.name.trim() === '' ? 'an exercise' : `“${member.name}”`
          if (member.lines.length === 0) {
            out.set(
              `${workout.name.trim() === '' ? 'A workout' : workout.name} · ${label} has no set lines yet, so it plans nothing`,
              exerciseAnchor(index, member.name),
            )
          }
          for (const line of member.lines) {
            if (line.type !== 'set') {
              continue
            }
            const downward = (held: typeof line.primary) =>
              held.type === 'call' &&
              held.callee.type === 'name' &&
              held.callee.name === 'range' &&
              held.args[0]?.type === 'number' &&
              held.args[1]?.type === 'number' &&
              held.args[0].value > held.args[1].value
            if (downward(line.primary) || (line.weight !== null && downward(line.weight))) {
              out.set(
                `${label} asks for a range that runs downward — put the smaller number first`,
                exerciseAnchor(index, member.name),
              )
            }
            if (line.count?.type === 'number' && line.count.value > 100) {
              out.set(
                `${label} asks for ${String(line.count.value)} sets in one line — is that right?`,
                exerciseAnchor(index, member.name),
              )
            }
          }
        }
      })
    })
    node.workouts.forEach((workout, index) => {
      try {
        const plan = new Evaluator(node, EMPTY_HISTORY).workout(workout.name)
        for (const exercise of plan?.exercises ?? []) {
          if (exercise.error !== undefined) {
            out.set(
              `${workout.name === '' ? 'Workout' : workout.name} · ${
                exercise.name === '' ? 'exercise' : exercise.name
              }: ${exercise.error.replace(/\s*\(line \d+, column \d+\)/g, '')}`,
              exerciseAnchor(index, exercise.name),
            )
          }
        }
      } catch {
        out.set(
          `${workout.name === '' ? 'A workout' : workout.name} is too big to plan — check for enormous numbers`,
          workoutAnchor(index),
        )
      }
    })
    return [...out.entries()].map(([text, anchor]) => ({ text, anchor }))
  }, [node])

  // Saving with known problems deserves one look first — nobody should find them at the gym.
  const requestSave = () => {
    if (troubles.length > 0) {
      setConfirmSave(true)
    } else {
      void save()
    }
  }

  // The shortcuts a desktop editor is expected to have: save, undo, redo.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) {
        return
      }
      const key = event.key.toLowerCase()
      if (key === 's') {
        event.preventDefault()
        if (dirty.current && problem === null) {
          requestSave()
        }
      } else if (key === 'z') {
        // Every field writes into the script as it is typed, so the script's own history is
        // the one undo everything shares — including the script drawer.
        event.preventDefault()
        if (event.shiftKey) {
          redo()
        } else {
          undo()
        }
      } else if (key === 'y') {
        event.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  })

  const blocker = useBlocker({
    shouldBlockFn: () => dirty.current,
    enableBeforeUnload: () => dirty.current,
    withResolver: true,
  })

  // A script broken in the drawer needs the drawer to fix it; opening it is the least the
  // editor can do on the way to showing where.
  const broken = problem !== null
  const [seenBroken, setSeenBroken] = useState(broken)
  if (seenBroken !== broken) {
    setSeenBroken(broken)
    if (broken) {
      setPanel('script')
    }
  }

  const headerNode = node ?? lastGood
  const schedule = headerNode === null ? 'free' : scheduleOf(headerNode)
  // The catalog and every name already logged: an exercise nobody put in the catalog is still
  // an exercise you have done, and a template is where you say you mean to do it again. The
  // board folds in what the template itself says, per exercise, so an exercise used two cards
  // over reads as known — but never the one being typed.
  const names = useMemo(() => {
    const all = new Set(state.exercises.map((exercise) => exercise.name))
    effectiveRecentExercises(state.recentExercises, state.sets).forEach((exercise) => {
      all.add(exercise.name)
    })
    return [...all].sort((one, other) => one.toLowerCase().localeCompare(other.toLowerCase()))
  }, [state.exercises, state.recentExercises, state.sets])
  useEffect(() => {
    setExerciseNames([...names])
  }, [names])
  const history = useRunHistory(node, templateId)

  // What strict knew — the cycle, who sat on which day — survives a trip through the other
  // schedules, outside the script, and comes back when strict does.
  const strictMemory = useRef<{ cycle: number; days: (number | null)[] } | null>(null)
  const pickSchedule = (option: (typeof SCHEDULES)[number]) => {
    if (node === null || scheduleOf(node) === option) {
      return
    }
    if (scheduleOf(node) === 'strict') {
      strictMemory.current = {
        cycle: cycleOf(node) ?? 7,
        days: node.workouts.map((workout) => dayOf(workout)),
      }
    }
    rewrite(onSchedule(node, option, option === 'strict' ? strictMemory.current : null))
  }

  // Clicking a trouble goes to the card it is about, which answers with a pulse.
  const jumpTo = (anchor: string | null) => {
    if (anchor === null) {
      return
    }
    const element = document.getElementById(anchor)
    if (element === null) {
      return
    }
    element.scrollIntoView({ block: 'center', behavior: 'smooth' })
    element.classList.remove('flash-target')
    // Reflow so the animation restarts even when the same card is asked twice.
    void element.offsetWidth
    element.classList.add('flash-target')
    setTimeout(() => {
      element.classList.remove('flash-target')
    }, 1300)
  }

  // The banner waits a beat before appearing, so a state the editor itself just created — a
  // fresh unnamed exercise, mid-typing — is not read out as a scolding. Going quiet is instant.
  const [showTroubles, setShowTroubles] = useState(false)
  if (showTroubles && troubles.length === 0) {
    setShowTroubles(false)
  }
  useEffect(() => {
    if (troubles.length === 0) {
      return
    }
    const timer = setTimeout(() => {
      setShowTroubles(true)
    }, 1200)
    return () => {
      clearTimeout(timer)
    }
  }, [troubles])

  if (missing) {
    return (
      <div className="grid min-h-[calc(100dvh-4rem)] place-items-center px-6">
        {checked ? (
          <div className="space-y-3 text-center">
            <p className="text-lg">This template is not in your library.</p>
            <Link
              to="/"
              search={{ dialog: 'templates' }}
              mask={{ to: DIALOG_PATHS.templates }}
              className="text-accent hover:underline"
            >
              Back to templates
            </Link>
          </div>
        ) : (
          <p className="text-ink-muted">Loading template…</p>
        )}
      </div>
    )
  }

  const board = lastGood
  const shownPanel = panel ?? heldPanel

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col">
      <div
        className={`flex min-h-0 flex-1 flex-col pr-16 transition-[padding] duration-300 ${panel !== null ? 'xl:pr-[42rem]' : ''}`}
      >
        <header className="z-20 border-b border-white/5 bg-surface/95 px-4 py-3 backdrop-blur sm:px-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Link
              to="/"
              search={{ dialog: 'templates' }}
              mask={{ to: DIALOG_PATHS.templates }}
              className="shrink-0 text-sm text-ink-muted hover:text-ink"
            >
              Templates
            </Link>
            <span aria-hidden className="text-ink-muted">
              ›
            </span>
            {node === null ? (
              <span className="flex-1 text-lg">{template?.name ?? 'Template'}</span>
            ) : (
              <input
                aria-label="Template name"
                value={node.name}
                placeholder="Template name"
                className="min-w-40 flex-1 rounded-lg bg-transparent px-2 py-1 text-xl font-medium outline-none transition-colors placeholder:text-ink-muted hover:bg-hover focus:bg-surface-raised"
                onFocus={(event) => {
                  // The first thing anyone does with a name like "New template" is replace it.
                  event.currentTarget.select()
                }}
                onChange={(event) => {
                  rewrite({ ...node, name: event.target.value })
                }}
              />
            )}
            <button
              type="button"
              aria-label="Undo"
              title="Undo (⌘Z)"
              data-undo-control
              disabled={past.length === 0}
              className="grid size-9 place-items-center rounded-full text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
              onClick={undo}
            >
              <UndoIcon aria-hidden className="size-5" />
            </button>
            <button
              type="button"
              aria-label="Redo"
              title="Redo (⇧⌘Z)"
              data-undo-control
              disabled={future.length === 0}
              className="grid size-9 place-items-center rounded-full text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
              onClick={redo}
            >
              <RedoIcon aria-hidden className="size-5" />
            </button>
            <span
              className={`w-40 shrink-0 text-right text-sm ${
                problem !== null ? 'text-danger' : 'text-ink-muted'
              }`}
            >
              {problem !== null ? 'Script has a problem' : saved ? 'Saved' : 'Unsaved changes'}
            </span>
            <span
              title={
                problem !== null
                  ? 'The script has a problem — fix it before saving'
                  : saved
                    ? 'Everything is saved'
                    : 'Save (⌘S)'
              }
            >
              <button
                type="button"
                className="rounded-full bg-accent px-5 py-2 text-base font-medium text-accent-ink transition-opacity disabled:cursor-not-allowed disabled:opacity-30"
                disabled={saved || problem !== null}
                onClick={requestSave}
              >
                Save
              </button>
            </span>
            {/*
              Publishing is a separate act from saving: the draft is yours to work in, and what
              Explore offers only moves when you say so. A script that will not parse cannot go.
            */}
            {template !== undefined ? (
              <>
                <button
                  type="button"
                  aria-busy={publishing || undefined}
                  disabled={
                    !publishing &&
                    (problem !== null ||
                      (template.publishedVersionId != null && template.publishedIsCurrent === true))
                  }
                  title={
                    problem !== null
                      ? 'The script has a problem — fix it before publishing'
                      : 'Publish to Explore'
                  }
                  className="relative rounded-full border border-outline px-5 py-2 text-base font-medium transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-30"
                  onClick={() => {
                    void publishThis()
                  }}
                >
                  <Working shown={publishingShown}>
                    {template.publishedVersionId == null
                      ? 'Publish'
                      : template.publishedIsCurrent === true
                        ? 'Published'
                        : 'Publish changes'}
                  </Working>
                </button>
                {/* Taking it out is its own control, reachable whether or not there are edits. */}
                {template.publishedVersionId != null ? (
                  <button
                    type="button"
                    disabled={publishing}
                    className="rounded-full px-4 py-2 text-base font-medium text-ink-muted transition-colors hover:bg-hover hover:text-ink disabled:opacity-30"
                    onClick={() => {
                      setUnpublishing(true)
                    }}
                  >
                    Unpublish
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
          {publishTrouble !== null ? (
            <p className="pt-2 text-sm text-danger">{publishTrouble}</p>
          ) : null}

          {/*
            What the program is, in your own words — the one thing Explore shows about it that
            the script's shape cannot say. It lives in the script like the name does, so it
            travels with a copy and is versioned with everything else.
          */}
          {node !== null ? (
            <textarea
              aria-label="Description"
              value={templateDescription(node) ?? ''}
              rows={describing ? 4 : 1}
              placeholder="What is this program? Shown in Explore."
              className="mt-2 w-full resize-none rounded-lg bg-transparent px-2 py-1 text-sm outline-none transition-colors placeholder:text-ink-muted hover:bg-hover focus:bg-surface-raised"
              onFocus={() => {
                setDescribing(true)
              }}
              onBlur={() => {
                setDescribing(false)
              }}
              onChange={(event) => {
                const written = event.target.value
                rewrite(
                  withProp(
                    node,
                    'description',
                    written.trim().length === 0
                      ? null
                      : { type: 'text', value: written, at: { line: 0, column: 0 } },
                  ),
                )
              }}
            />
          ) : null}

          {headerNode === null ? null : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-2">
              {/*
                What the schedule is, until you go looking for the others. The ones not chosen
                fold away to nothing and open again under the pointer — or under the keyboard,
                which reaches them whether they are folded or not. A column of `0fr` is what
                animates: `auto` has no width to travel from, and a fixed one would guess.

                Focus holds it open only when the focus is one somebody can see: a chip keeps
                the focus after it is clicked, and `:focus-within` would leave the set open
                long after the pointer had gone.

                Opening is immediate and closing waits, which is one delay written twice: the
                folded state carries it, and the open state sets it back to nothing — so the
                delay is only ever spent on the way out, where a pointer that strays deserves
                a moment to come back.
              */}
              <div role="group" aria-label="Schedule" className="group/schedule flex items-center">
                {SCHEDULES.map((option) => (
                  <span
                    key={option}
                    className={
                      schedule === option
                        ? 'grid'
                        : 'grid grid-cols-[0fr] transition-[grid-template-columns] delay-300 duration-200 ease-out group-hover/schedule:grid-cols-[1fr] group-hover/schedule:delay-0 group-[&:has(:focus-visible)]/schedule:grid-cols-[1fr] group-[&:has(:focus-visible)]/schedule:delay-0'
                    }
                  >
                    <span
                      className={`min-w-0 overflow-hidden pr-1 ${
                        schedule === option
                          ? ''
                          : 'opacity-0 transition-opacity delay-300 duration-200 group-hover/schedule:opacity-100 group-hover/schedule:delay-0 group-[&:has(:focus-visible)]/schedule:opacity-100 group-[&:has(:focus-visible)]/schedule:delay-0'
                      }`}
                    >
                      <button
                        type="button"
                        aria-pressed={schedule === option}
                        title={SCHEDULE_CAPTION[option]}
                        disabled={node === null}
                        className={`rounded-full px-4 py-1.5 text-sm capitalize transition-colors disabled:opacity-50 ${
                          schedule === option
                            ? 'bg-accent text-accent-ink'
                            : 'bg-surface-raised text-ink-muted hover:text-ink'
                        }`}
                        onClick={() => {
                          pickSchedule(option)
                        }}
                      >
                        {option}
                      </button>
                    </span>
                  </span>
                ))}
              </div>
              {schedule === 'strict' && node !== null ? (
                (cycleOf(node) ?? 7) % 7 === 0 ? (
                  <WholeNumberField
                    label="Weeks in the cycle"
                    value={Math.max(1, Math.ceil((cycleOf(node) ?? 7) / 7))}
                    min={1}
                    max={12}
                    onValue={(weeks) => {
                      rewrite(withProp(node, 'cycle', number(weeks * 7, UNITS.reps)))
                    }}
                  />
                ) : (
                  // A hand-written cycle need not be whole weeks; the control tells the truth.
                  <WholeNumberField
                    label="Days in the cycle"
                    value={Math.max(1, cycleOf(node) ?? 7)}
                    min={1}
                    max={90}
                    onValue={(days) => {
                      rewrite(withProp(node, 'cycle', number(days, UNITS.reps)))
                    }}
                  />
                )
              ) : null}
              <span className="text-xs text-ink-muted">{SCHEDULE_CAPTION[schedule] ?? ''}</span>
            </div>
          )}

          {troubles.length === 0 || !showTroubles ? null : (
            <div className="mt-2 rounded-xl bg-danger/10 px-3 py-2 text-sm text-danger">
              <p>This template will not plan cleanly:</p>
              <ul className="list-inside list-disc">
                {troubles.slice(0, 3).map((trouble) => (
                  <li key={trouble.text}>
                    <button
                      type="button"
                      className="text-left underline-offset-2 hover:underline"
                      onClick={() => {
                        jumpTo(trouble.anchor)
                      }}
                    >
                      {trouble.text}
                    </button>
                  </li>
                ))}
              </ul>
              {troubles.length > 3 ? <p>…and {troubles.length - 3} more.</p> : null}
            </div>
          )}
        </header>

        {/* The board is the scroller, not the page, so a rest day can ride along a long week. */}
        <div className="scroll-x scroll-y min-h-0 flex-1 px-4 py-5 sm:px-6">
          {node !== null ? (
            <TemplateBoard node={node} onNode={rewrite} names={names} />
          ) : board !== null ? (
            <div>
              <p className="pb-4 text-sm text-danger">
                The script does not parse, so the board shows the last state that did. Put the
                script right and it comes back to life.
              </p>
              <div aria-hidden className="pointer-events-none select-none opacity-40">
                <TemplateBoard node={board} onNode={() => undefined} names={names} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-ink-muted">
              The script does not parse, so there is no board to show. Put it right in the script
              drawer and the board appears.
            </p>
          )}
        </div>
      </div>

      {/* The drawer the rail's tools slide in on: full height, over the board, never with it. */}
      <aside
        aria-hidden={panel === null}
        className={`fixed bottom-0 right-16 top-16 z-30 flex w-[min(38rem,calc(100vw-4rem))] flex-col border-l border-white/5 bg-surface-raised shadow-2xl transition-transform duration-300 ${
          panel !== null ? 'translate-x-0' : 'pointer-events-none translate-x-[calc(100%+4rem)]'
        }`}
      >
        {shownPanel === 'script' ? (
          <div className="flex min-h-0 flex-1 flex-col p-4">
            <div className="min-h-0 flex-1">
              <CodeEditor
                label="Script"
                height="100%"
                value={script}
                problem={problemInfo}
                onChange={(next) => {
                  hold(next, 'script')
                }}
              />
            </div>
            {problem === null ? null : <p className="pt-2 text-sm text-danger">{problem}</p>}
          </div>
        ) : shownPanel === 'trial' ? (
          node === null ? (
            <p className="p-4 text-sm text-ink-muted">
              A script that does not parse has nothing to try.
            </p>
          ) : (
            <TemplateTrialPanel
              node={node}
              base={history}
              units={units}
              trial={trial}
              onTrial={setTrial}
            />
          )
        ) : null}
      </aside>

      <nav
        aria-label="Editor tools"
        className="fixed bottom-0 right-0 top-16 z-30 flex w-16 flex-col items-center gap-1 border-l border-white/5 bg-surface py-3"
      >
        <RailButton
          label="Script"
          active={panel === 'script'}
          alert={problem !== null}
          onPress={() => {
            setPanel(panel === 'script' ? null : 'script')
          }}
        >
          <CodeIcon aria-hidden className="size-6" />
        </RailButton>
        <RailButton
          label="Try it"
          active={panel === 'trial'}
          onPress={() => {
            setPanel(panel === 'trial' ? null : 'trial')
          }}
        >
          <PlayIcon aria-hidden className="size-6" />
        </RailButton>
      </nav>

      {blocker.status === 'blocked' ? (
        <ConfirmDialog
          title="Leave without saving?"
          body="The changes you made to this template are not saved."
          confirm="Discard changes"
          alt={problem === null ? 'Save and leave' : undefined}
          onAlt={
            problem === null
              ? // Waited on in the dialog, which works while the save does and stays if it fails.
                async () => {
                  await save()
                  blocker.proceed()
                }
              : undefined
          }
          onConfirm={() => {
            dirty.current = false
            blocker.proceed()
          }}
          onDismiss={() => {
            blocker.reset()
          }}
        />
      ) : null}

      {unpublishing ? (
        <ConfirmDialog
          title="Unpublish this template?"
          body="It leaves Explore, and nobody new can start it. Everybody already running it carries on, on the version they are on."
          confirm="Unpublish"
          onConfirm={async () => {
            await unpublishThis()
            setUnpublishing(false)
          }}
          onDismiss={() => {
            setUnpublishing(false)
          }}
        />
      ) : null}

      {confirmSave ? (
        <ConfirmDialog
          title="Save with problems?"
          body="This template will not plan cleanly until its problems are fixed. It can still be saved and finished later."
          confirm="Save anyway"
          onConfirm={() => {
            setConfirmSave(false)
            void save()
          }}
          onDismiss={() => {
            setConfirmSave(false)
          }}
        />
      ) : null}
    </div>
  )
}

function RailButton({
  label,
  active,
  alert,
  onPress,
  children,
}: {
  label: string
  active: boolean
  alert?: boolean
  onPress: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      className={`relative flex w-14 flex-col items-center gap-0.5 rounded-2xl py-2 transition-colors ${
        active ? 'bg-accent/15 text-accent' : 'text-ink-muted hover:bg-hover hover:text-ink'
      }`}
      onClick={onPress}
    >
      {children}
      <span className="text-[0.6875rem]">{label}</span>
      {alert === true ? (
        <span aria-hidden className="absolute right-2.5 top-1.5 size-2 rounded-full bg-danger" />
      ) : null}
    </button>
  )
}

/**
 * The history a trial can start from: the run underway on this template, and every set ever
 * logged of the exercises it names. Without a run there is nothing behind it, and the trial
 * opens on a fresh start.
 */
function useRunHistory(node: TemplateNode | null, templateId: string): History {
  const state = useDataState()
  const service = useDataService()
  // Held with the request it answered, so a history fetched for one template — or for one run —
  // is never read as another's while the next fetch is still out.
  const [fetched, setFetched] = useState<{ key: string; history: History } | null>(null)

  const names = useMemo(
    () =>
      (node?.workouts ?? [])
        .flatMap((workout) =>
          workout.items.flatMap((item) =>
            item.type === 'exercise'
              ? [item.exercise.name]
              : item.exercises.map((held) => held.name),
          ),
        )
        .filter((name, index, all) => name !== '' && all.indexOf(name) === index)
        .sort(),
    [node],
  )
  const running =
    state.enrollments.find(
      (enrollment) => enrollment.endedAt === null && enrollment.templateId === templateId,
    ) ?? null
  const runId = running?.id ?? null
  const key = `${runId ?? ''}|${names.join(' ')}`

  useEffect(() => {
    if (names.length === 0) {
      return
    }
    let alive = true
    void service
      .templateContext(names, runId)
      .then((context) => {
        if (alive) {
          setFetched({ key, history: toHistory(context) })
        }
      })
      .catch(() => {
        // Nothing behind it is a fair starting point; the trial still runs forward.
        if (alive) {
          setFetched({ key, history: EMPTY_HISTORY })
        }
      })
    return () => {
      alive = false
    }
  }, [service, key, names, runId])

  return fetched?.key === key ? fetched.history : EMPTY_HISTORY
}

/**
 * Changing the schedule. Off strict, days and the cycle mean nothing and leave the script;
 * going strict, every workout takes a day — remembered from the last strict spell where one
 * was, dealt out where none was.
 */
function onSchedule(
  node: TemplateNode,
  option: (typeof SCHEDULES)[number],
  memory: { cycle: number; days: (number | null)[] } | null,
): TemplateNode {
  const named = withProp(node, 'schedule', {
    type: 'name',
    name: option,
    at: { line: 0, column: 0 },
  })
  if (option !== 'strict') {
    return {
      ...withProp(named, 'cycle', null),
      workouts: named.workouts.map((workout) => withProp(workout, 'day', null)),
    }
  }
  const cycle =
    memory?.cycle ?? cycleOf(named) ?? 7 * Math.max(1, Math.ceil(named.workouts.length / 7))
  const taken = new Set<number>()
  // What each workout would sit on: its own day if it has one, else the remembered one.
  const wished = named.workouts.map((workout, index) => {
    const own = dayOf(workout)
    const kept = own !== null && own >= 0 && own < cycle ? own : (memory?.days[index] ?? null)
    return kept !== null && kept >= 0 && kept < cycle ? kept : null
  })
  const days = wished.map((wish) => {
    if (wish !== null && !taken.has(wish)) {
      taken.add(wish)
      return wish
    }
    return null
  })
  let free = 0
  const nextFree = () => {
    while (taken.has(free)) {
      free += 1
    }
    taken.add(free)
    return free
  }
  return {
    ...withProp(named, 'cycle', number(cycle, UNITS.reps)),
    workouts: named.workouts.map((workout, index) =>
      withProp(workout, 'day', number(days[index] ?? Math.min(nextFree(), cycle - 1), UNITS.reps)),
    ),
  }
}
